/**
 * ClientPermissionRoute — the customer portal's own route guard.
 *
 * Sibling of `PermissionRoute`, and deliberately not a branch of it: that guard reads
 * `permissionStore`, which resolves to an EMPTY set for a client session by design (decision Р31).
 * This one reads `useClientCapabilities`, the portal's own source.
 *
 * @example
 * ```tsx
 * <ClientPermissionRoute permission={CPK.Employees.View}>
 *   <ClientEmployeesPage />
 * </ClientPermissionRoute>
 * ```
 */

import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { ROUTES } from '@/constants';
import type { ClientPermissionKey } from '@/constants/clientPermissions';
import { useClientCapabilities } from '@/hooks/api/clientPortal/useClientCapabilities';
import { PageLoading } from '@/components/ui';

export interface ClientPermissionRouteProps {
  children: ReactNode;
  /** The key the caller's client role must hold in their active company. */
  permission: ClientPermissionKey;
}

export function ClientPermissionRoute({
  children,
  permission,
}: ClientPermissionRouteProps): JSX.Element {
  const caps = useClientCapabilities();

  // Not a redirect: a direct link opened in a fresh tab arrives before the profile does, and
  // bouncing on "not known yet" sends people to the dashboard for no reason at all.
  if (!caps.isLoaded && !caps.isError) {
    return <PageLoading />;
  }

  if (caps.can(permission)) {
    return <>{children}</>;
  }

  // The dashboard, NOT `/forbidden`. That route lives outside `scope: '/client/'` in the portal's
  // web app manifest, so in the installed app a customer who lands on it is thrown out of the app
  // itself — the same reason `ClientRoute` refuses to send them there.
  return <Navigate to={ROUTES.client.dashboard} replace state={{ accessDenied: true }} />;
}

export default ClientPermissionRoute;
